import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Stack, TextField, Select, MenuItem,
  FormControl, InputLabel, Paper, CircularProgress,
  Alert, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Divider, Tooltip, IconButton, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import { Send, CheckCircle, Schedule, Notifications as NotifIcon, Refresh, Close, Info, Person, People, Groups } from '@mui/icons-material';
import { notificationsAPI, usersAPI, batchesAPI } from '../services/api';
import PaginationBar from '../components/PaginationBar';
import PageHeader from '../components/PageHeader';

const TYPE_LABELS = {
  custom: 'Custom',
  workout_reminder: 'Workout Reminder',
  goal_update: 'Goal Update',
  achievement: 'Achievement',
  gym_announcement: 'Gym Announcement',
};

const TYPE_COLORS = {
  custom: 'default',
  workout_reminder: 'primary',
  goal_update: 'success',
  achievement: 'warning',
  gym_announcement: 'info',
};

const EVENT_TYPE_LABELS = {
  member_enrolled: 'Member Enrolled',
  member_left: 'Member Left',
  fee_paid: 'Fee Paid',
  fee_failed: 'Payment Failed',
  plan_downloaded: 'Plan Downloaded',
  staff_added: 'Staff Added',
  member_rejoined: 'Member Rejoined',
  challenge_enrolled: 'Challenge Enrollment',
  new_lead: 'New Lead',
  lead_converted: 'Lead Converted',
  feedback_received: 'Feedback Received',
  attendance_marked: 'Attendance Marked',
  slot_booked: 'Slot Booked',
  low_attendance_alert: 'Low Attendance',
  batch_created: 'Batch Created',
  member_milestone: 'Member Milestone',
};

const EVENT_TYPE_COLORS = {
  member_enrolled: 'success',
  member_left: 'error',
  fee_paid: 'success',
  fee_failed: 'error',
  plan_downloaded: 'info',
  staff_added: 'info',
  member_rejoined: 'warning',
  challenge_enrolled: 'primary',
  new_lead: 'info',
  lead_converted: 'success',
  feedback_received: 'secondary',
  attendance_marked: 'info',
  slot_booked: 'info',
  low_attendance_alert: 'warning',
  batch_created: 'info',
  member_milestone: 'success',
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function Notifications({
  activeTab,
  hideHeader = false,
  hideTabs = false,
  onTabChange,
}) {
  const [internalTabValue, setInternalTabValue] = useState(0);
  const tabValue = activeTab !== undefined ? activeTab : internalTabValue;
  const handleTabChange = (e, v) => {
    setInternalTabValue(v);
    if (onTabChange) onTabChange(v);
  };
  const [users, setUsers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [eventNotifications, setEventNotifications] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [sending, setSending] = useState(false);
  const [alert, setAlert] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Recipient selection mode
  const [sendMode, setSendMode] = useState('selected'); // 'selected' | 'group' | 'all'
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);

  const [eventPage, setEventPage] = useState(1);
  const [eventTotalPages, setEventTotalPages] = useState(1);
  const [eventTotal, setEventTotal] = useState(0);
  const [eventLimit, setEventLimit] = useState(10);

  const [formData, setFormData] = useState({
    title: '',
    body: '',
    type: 'custom',
    scheduledFor: '',
  });

  const fetchUsersAndBatches = async () => {
    try {
      setLoadingUsers(true);
      const [usersRes, batchesRes] = await Promise.allSettled([
        usersAPI.getAll({ limit: 1000 }),
        batchesAPI.getAll()
      ]);
      if (usersRes.status === 'fulfilled') {
        setUsers(usersRes.value.data.data || []);
      }
      if (batchesRes.status === 'fulfilled') {
        setBatches(batchesRes.value.data.data || []);
      }
    } catch {
      setAlert({ severity: 'error', text: 'Failed to load users or batches' });
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchSentNotifications = useCallback(async (p = 1) => {
    try {
      setLoadingList(true);
      const response = await notificationsAPI.getSent({ page: p, limit });
      setNotifications(response.data.data || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
    } catch {
      setAlert({ severity: 'error', text: 'Failed to load sent notifications' });
    } finally {
      setLoadingList(false);
    }
  }, [limit]);

  const fetchEventNotifications = useCallback(async (p = 1) => {
    try {
      setLoadingEvents(true);
      const response = await notificationsAPI.getEvents({ page: p, limit: eventLimit });
      setEventNotifications(response.data.data || []);
      setEventTotal(response.data.total || 0);
      setEventTotalPages(response.data.pages || 1);
    } catch (err) {
      console.error('Failed to load event notifications:', err);
    } finally {
      setLoadingEvents(false);
    }
  }, [eventLimit]);

  useEffect(() => {
    fetchUsersAndBatches();
    fetchSentNotifications(1);
    fetchEventNotifications(1);
  }, []);

  useEffect(() => {
    fetchSentNotifications(page);
  }, [fetchSentNotifications, page]);

  useEffect(() => {
    fetchEventNotifications(eventPage);
  }, [fetchEventNotifications, eventPage]);

  // Build available groups/segments from users and batches
  const availableGroups = useMemo(() => {
    const groups = [];

    // 1. Gym Batches
    if (batches && batches.length > 0) {
      batches.forEach(b => {
        const matchUsers = users.filter(u => u.batchName && u.batchName.toLowerCase() === b.name.toLowerCase());
        groups.push({
          id: `batch_${b._id || b.name}`,
          name: `Batch: ${b.name}${b.preferredTime ? ` (${b.preferredTime})` : ''}`,
          category: 'Batches / Time Slots',
          userIds: matchUsers.map(u => u._id),
          count: matchUsers.length,
        });
      });
    }

    // Include other batchNames
    const distinctBatchNames = [...new Set(users.map(u => u.batchName).filter(Boolean))];
    distinctBatchNames.forEach(bn => {
      if (!groups.some(g => g.name.toLowerCase().includes(bn.toLowerCase()))) {
        const matchUsers = users.filter(u => u.batchName === bn);
        groups.push({
          id: `batch_custom_${bn}`,
          name: `Batch: ${bn}`,
          category: 'Batches / Time Slots',
          userIds: matchUsers.map(u => u._id),
          count: matchUsers.length,
        });
      }
    });

    // 2. Membership Status
    const activeUsers = users.filter(u => u.membershipStatus === 'active' || (u.membershipEndDate && new Date(u.membershipEndDate) >= new Date()));
    groups.push({
      id: 'status_active',
      name: 'Active Members',
      category: 'Membership Status',
      userIds: activeUsers.map(u => u._id),
      count: activeUsers.length,
    });

    const expiredUsers = users.filter(u => u.membershipStatus === 'expired' || (u.membershipEndDate && new Date(u.membershipEndDate) < new Date()));
    groups.push({
      id: 'status_expired',
      name: 'Expired / Due Members',
      category: 'Membership Status',
      userIds: expiredUsers.map(u => u._id),
      count: expiredUsers.length,
    });

    const pausedUsers = users.filter(u => u.membershipStatus === 'paused');
    if (pausedUsers.length > 0) {
      groups.push({
        id: 'status_paused',
        name: 'Paused Members',
        category: 'Membership Status',
        userIds: pausedUsers.map(u => u._id),
        count: pausedUsers.length,
      });
    }

    // 3. Gender
    const maleUsers = users.filter(u => u.gender === 'male');
    if (maleUsers.length > 0) {
      groups.push({
        id: 'gender_male',
        name: 'Male Members',
        category: 'Gender',
        userIds: maleUsers.map(u => u._id),
        count: maleUsers.length,
      });
    }

    const femaleUsers = users.filter(u => u.gender === 'female');
    if (femaleUsers.length > 0) {
      groups.push({
        id: 'gender_female',
        name: 'Female Members',
        category: 'Gender',
        userIds: femaleUsers.map(u => u._id),
        count: femaleUsers.length,
      });
    }

    // 4. Premium Subscription
    const premiumUsers = users.filter(u => u.isPremium);
    if (premiumUsers.length > 0) {
      groups.push({
        id: 'premium_vip',
        name: 'Premium VIP Members',
        category: 'Subscription Plan',
        userIds: premiumUsers.map(u => u._id),
        count: premiumUsers.length,
      });
    }

    // 5. Staff & Roles
    const trainers = users.filter(u => ['trainer', 'coach'].includes(u.role));
    if (trainers.length > 0) {
      groups.push({
        id: 'role_trainers',
        name: 'Trainers & Coaches',
        category: 'Staff & Roles',
        userIds: trainers.map(u => u._id),
        count: trainers.length,
      });
    }

    return groups;
  }, [users, batches]);

  // Extract unique user IDs from selected groups
  const groupUserIds = useMemo(() => {
    const idSet = new Set();
    selectedGroups.forEach(grp => {
      grp.userIds.forEach(id => idSet.add(id));
    });
    return Array.from(idSet);
  }, [selectedGroups]);

  const update = (field, value) => setFormData(f => ({ ...f, [field]: value }));

  const resetForm = () => {
    setFormData({ title: '', body: '', type: 'custom', scheduledFor: '' });
    setSelectedUsers([]);
    setSelectedGroups([]);
  };

  const handleSendNotification = async () => {
    if (!formData.title || !formData.body) {
      setAlert({ severity: 'warning', text: 'Please fill in title and body' });
      return;
    }

    if (sendMode === 'selected' && selectedUsers.length === 0) {
      setAlert({ severity: 'warning', text: 'Please select at least one recipient user' });
      return;
    }

    if (sendMode === 'group' && selectedGroups.length === 0) {
      setAlert({ severity: 'warning', text: 'Please select at least one group or batch' });
      return;
    }

    try {
      setSending(true);
      if (sendMode === 'all') {
        const userIds = users.map(u => u._id);
        await notificationsAPI.sendBulk({
          userIds,
          title: formData.title,
          body: formData.body,
          type: formData.type,
        });
        setAlert({ severity: 'success', text: `Push notification sent to all ${userIds.length} users successfully` });
      } else if (sendMode === 'group') {
        if (groupUserIds.length === 0) {
          setAlert({ severity: 'warning', text: 'Selected group(s) have no members.' });
          setSending(false);
          return;
        }
        await notificationsAPI.sendBulk({
          userIds: groupUserIds,
          title: formData.title,
          body: formData.body,
          type: formData.type,
        });
        setAlert({ severity: 'success', text: `Push notification sent to ${groupUserIds.length} members across ${selectedGroups.length} selected group(s)` });
      } else if (selectedUsers.length === 1) {
        await notificationsAPI.send({
          userId: selectedUsers[0]._id,
          title: formData.title,
          body: formData.body,
          type: formData.type,
          scheduledFor: formData.scheduledFor || undefined,
        });
        setAlert({ severity: 'success', text: `Push notification sent to ${selectedUsers[0].name || 'user'} successfully` });
      } else {
        const userIds = selectedUsers.map(u => u._id);
        await notificationsAPI.sendBulk({
          userIds,
          title: formData.title,
          body: formData.body,
          type: formData.type,
        });
        setAlert({ severity: 'success', text: `Push notification sent to ${userIds.length} selected users successfully` });
      }

      resetForm();
      setPage(1);
      fetchSentNotifications(1);
    } catch (err) {
      setAlert({ severity: 'error', text: err.response?.data?.message || 'Failed to send push notification' });
    } finally {
      setSending(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleString();
  };

  return (
    <Box>
      {/* Header */}
      {!hideHeader && (
        <PageHeader
          title="Push Notifications"
          subtitle="Compose push notifications and broadcast updates directly to member mobile devices."
          gradient="linear-gradient(90deg, #1D4ED8, #60A5FA)"
          actions={<Button variant="outlined" size="small" startIcon={<Refresh />} onClick={() => { fetchSentNotifications(page); fetchEventNotifications(); }}>Refresh</Button>}
        />
      )}

      {alert && (
        <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 3 }}>
          {alert.text}
        </Alert>
      )}

      {/* Tabs */}
      <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        {!hideTabs && (
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Push Notification" />
            <Tab label={`Event Notifications`} />
          </Tabs>
        )}

        <TabPanel value={tabValue} index={0}>
          {/* Compose form */}
          <Box sx={{ px: 3, pb: 3 }}>
            <Typography variant="body1" fontWeight={700} sx={{ mb: 2 }}>
              Compose Push Notification
            </Typography>
            <Stack spacing={2.5}>
              {/* Recipient Selection 3-Way Toggle */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom color="text.secondary">
                  Recipient Audience
                </Typography>
                <ToggleButtonGroup
                  value={sendMode}
                  exclusive
                  onChange={(e, val) => val && setSendMode(val)}
                  size="small"
                >
                  <ToggleButton value="selected" sx={{ px: 2, py: 0.8, fontWeight: 600, textTransform: 'none' }}>
                    <Person sx={{ fontSize: 18, mr: 1 }} /> Specific Users
                  </ToggleButton>
                  <ToggleButton value="group" sx={{ px: 2, py: 0.8, fontWeight: 600, textTransform: 'none' }}>
                    <Groups sx={{ fontSize: 18, mr: 1 }} /> Member Groups & Batches
                  </ToggleButton>
                  <ToggleButton value="all" sx={{ px: 2, py: 0.8, fontWeight: 600, textTransform: 'none' }}>
                    <People sx={{ fontSize: 18, mr: 1 }} /> All Users ({users.length})
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Mode A: Specific Users */}
              {sendMode === 'selected' && (
                <Autocomplete
                  multiple
                  options={users}
                  loading={loadingUsers}
                  value={selectedUsers}
                  onChange={(e, val) => setSelectedUsers(val)}
                  getOptionLabel={(option) => `${option.name || 'Unnamed'} (${option.email || option.phone || 'No contact'})`}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Select Recipients"
                      placeholder={selectedUsers.length === 0 ? "Search members by name or email..." : "Add more members..."}
                      helperText="Select one or multiple members to receive this push notification."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option._id}
                        label={`${option.name || 'Member'} (${option.email || 'user'})`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))
                  }
                />
              )}

              {/* Mode B: Groups & Batches */}
              {sendMode === 'group' && (
                <Autocomplete
                  multiple
                  options={availableGroups}
                  groupBy={(option) => option.category}
                  getOptionLabel={(option) => `${option.name} (${option.count} members)`}
                  value={selectedGroups}
                  onChange={(e, val) => setSelectedGroups(val)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Select Group(s) / Batch(es)"
                      placeholder={selectedGroups.length === 0 ? "Choose batches or membership groups..." : "Add more groups..."}
                      helperText="Select one or multiple batches or membership groups to target."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={`${option.name} (${option.count})`}
                        size="small"
                        color="primary"
                      />
                    ))
                  }
                />
              )}

              {sendMode === 'group' && selectedGroups.length > 0 && (
                <Typography variant="caption" color="primary.main" sx={{ fontWeight: 700 }}>
                  ✓ {groupUserIds.length} unique recipient member{groupUserIds.length > 1 ? 's' : ''} across {selectedGroups.length} selected group{selectedGroups.length > 1 ? 's' : ''}
                </Typography>
              )}

              <TextField
                fullWidth size="small" label="Notification Title *"
                value={formData.title} onChange={e => update('title', e.target.value)}
                placeholder="e.g. Special Workout Challenge!"
              />

              <TextField
                fullWidth size="small" label="Notification Body *" multiline rows={3}
                value={formData.body} onChange={e => update('body', e.target.value)}
                placeholder="Enter notification message to be displayed on member devices..."
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select value={formData.type} label="Type" onChange={e => update('type', e.target.value)}>
                    {Object.entries(TYPE_LABELS).map(([v, l]) => (
                      <MenuItem key={v} value={v}>{l}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {sendMode === 'selected' && selectedUsers.length === 1 && (
                  <TextField
                    fullWidth size="small" label="Schedule For (optional)"
                    type="datetime-local" value={formData.scheduledFor}
                    onChange={e => update('scheduledFor', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              </Stack>

              <Divider sx={{ my: 1 }} />

              <Box>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <Send />}
                  onClick={handleSendNotification}
                  disabled={
                    sending ||
                    loadingUsers ||
                    (sendMode === 'selected' && selectedUsers.length === 0) ||
                    (sendMode === 'group' && selectedGroups.length === 0)
                  }
                  sx={{ px: 4, py: 1.2, fontWeight: 700, textTransform: 'none', borderRadius: 2 }}
                >
                  {sending
                    ? 'Sending Push Notification…'
                    : sendMode === 'all'
                    ? `Send Push Notification to All (${users.length} Users)`
                    : sendMode === 'group'
                    ? `Send Push Notification to Group (${groupUserIds.length} Members)`
                    : `Send Push Notification to ${selectedUsers.length} Member${selectedUsers.length > 1 ? 's' : ''}`
                  }
                </Button>
              </Box>
            </Stack>
          </Box>

          {/* Sent notifications list */}
          <Divider />
          <Box sx={{ px: 3, pt: 3 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="body1" fontWeight={600}>Sent Notifications</Typography>
                {total > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {total} total
                  </Typography>
                )}
              </Box>
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={() => fetchSentNotifications(page)} disabled={loadingList}>
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            {loadingList ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            ) : notifications.length === 0 ? (
              <Box textAlign="center" py={6}>
                <NotifIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">No notifications sent yet</Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Recipient</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Body</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Sent At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {notifications.map(notif => (
                        <TableRow key={notif._id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {notif.userId?.name || '—'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {notif.userId?.email || ''}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{notif.title}</Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 260 }}>
                            <Typography variant="body2" color="text.secondary" sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {notif.body}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={TYPE_LABELS[notif.type] || notif.type}
                              color={TYPE_COLORS[notif.type] || 'default'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <CheckCircle sx={{ fontSize: 16, color: notif.isSent ? 'success.main' : 'text.disabled' }} />
                              <Typography variant="caption">
                                {notif.isSent ? 'Sent' : 'Pending'}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{formatDate(notif.sentAt)}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {totalPages > 1 && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <PaginationBar
                      page={page}
                      total={total}
                      limit={limit}
                      onPageChange={setPage}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Event notifications */}
          <Box sx={{ px: 3 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="body1" fontWeight={600}>Event Notifications</Typography>
                <Typography variant="caption" color="text.secondary">
                  Important events like member enrollments, payments, and staff changes
                </Typography>
                {eventTotal > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {eventTotal} total
                  </Typography>
                )}
              </Box>
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={() => fetchEventNotifications(eventPage)} disabled={loadingEvents}>
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            {loadingEvents ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            ) : eventNotifications.length === 0 ? (
              <Box textAlign="center" py={6}>
                <Info sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">No event notifications yet</Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Event Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Related Member</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Message</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {eventNotifications.map(notif => (
                        <TableRow key={notif._id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                          <TableCell>
                            <Chip
                              label={EVENT_TYPE_LABELS[notif.type] || notif.type}
                              color={EVENT_TYPE_COLORS[notif.type] || 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {notif.relatedUserId ? (
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {notif.relatedUserId.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {notif.relatedUserId.email}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{notif.title}</Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 260 }}>
                            <Typography variant="body2" color="text.secondary" sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {notif.body}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{formatDate(notif.createdAt)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => setSelectedEvent(notif)}
                              >
                                <Info fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {eventTotalPages > 1 && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <PaginationBar
                      page={eventPage}
                      total={eventTotal}
                      limit={eventLimit}
                      onPageChange={setEventPage}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* Event Details Dialog */}
      <Dialog open={Boolean(selectedEvent)} onClose={() => setSelectedEvent(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Event Details</Typography>
          <IconButton size="small" onClick={() => setSelectedEvent(null)}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Event Type</Typography>
                <Chip
                  label={EVENT_TYPE_LABELS[selectedEvent.type] || selectedEvent.type}
                  color={EVENT_TYPE_COLORS[selectedEvent.type] || 'default'}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Title</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>{selectedEvent.title}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Message</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>{selectedEvent.body}</Typography>
              </Box>
              {selectedEvent.relatedUserId && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Related Member</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {selectedEvent.relatedUserId.name} ({selectedEvent.relatedUserId.email})
                  </Typography>
                </Box>
              )}
              {selectedEvent.data && Object.keys(selectedEvent.data).length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Additional Data</Typography>
                  <Box sx={{ mt: 0.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(selectedEvent.data, null, 2)}
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Date</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>{formatDate(selectedEvent.createdAt)}</Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedEvent(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
